import React, { useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

// Components
import CardContent from "./CardContent";

// Utils
import { isAdmin } from "../../../utils/constants";
import { formatTimeFromNow } from "../../../utils";
import { useResponsive } from "../../../hooks/useResponsive";
import { useSetMultipleParams } from "../../../hooks/useSetMultipleParams";

function KanbanCard({
  project,
  onDragEnd,
  boardName,
  index,
  id,
  status,
  isTodo = false,
  style = false,
  isAssignedUser = false,
  isMyTaskCard = false,
  boardKey,
  isMyPlanner = false,
  openDetailCard = () => {},
}) {
  const { id: boardId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [setMultipleSearchParams] = useSetMultipleParams();
  const [collapsed] = useState(true);
  const currentUser = useSelector((state) => state.users.logInUser);
  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData(["boardName", boardId]);
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useResponsive("down", "md");
  const navigate = useNavigate();

  const {
    card,
    board_permission: projectBoardPermission,
    board_admin,
    board_name,
    key: boardUniqueKey,
  } = project || {};
  const {
    assigned_to_user,
    card_id,
    board_id,
    reporter,
    is_card_collaborator,
  } = card || {};
  const {
    first_name = "",
    last_name = "",
    user_id: assignedUserId,
  } = assigned_to_user || {};

  const tooltipName = `${first_name} ${last_name}`;
  const name = `${first_name[0] || ""}${last_name[0] || ""}`;
  const boardPermission = isMyTaskCard
    ? projectBoardPermission
    : boardName?.board_permission ?? {};

  const handleDetails = (event) => {
    // Check if modifier keys are pressed (Cmd/Ctrl or Shift)
    const isModifierPressed = event.metaKey || event.ctrlKey || event.shiftKey;

    if (isMyTaskCard || isMyPlanner) {
      if (isModifierPressed) {
        const params = new URLSearchParams();
        params.set("card_id", card_id);
        params.set("brd_id", board_id);
        window.open(
          `${window.location.pathname}?${params.toString()}`,
          "_blank"
        );
        event.stopPropagation();
        return;
      }

      setMultipleSearchParams([
        { name: "card_id", value: card_id },
        { name: "brd_id", value: board_id },
      ]);
      openDetailCard?.();
    } else if (!isAssignedUser) {
      const url = `/board/${boardKey || boardUniqueKey}/${
        boardKey || boardUniqueKey
      }-${card_id}?${searchParams.toString()}`;

      if (isModifierPressed) {
        // Open in new tab/window
        window.open(url, "_blank");
        event.stopPropagation();
        return;
      }

      navigate(url);
    } else {
      event.stopPropagation();
    }
  };

  const openSubtask = (event, id) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if modifier keys are pressed (Cmd/Ctrl or Shift)
    const isModifierPressed = event.metaKey || event.ctrlKey || event.shiftKey;

    if (isMyTaskCard) {
      if (isModifierPressed) {
        // For new tab/window, construct the URL and open it
        const params = new URLSearchParams();
        params.set("card_id", id);
        params.set("brd_id", board_id);
        window.open(
          `${window.location.pathname}?${params.toString()}`,
          "_blank"
        );
        event.stopPropagation();
        return;
      }
      setMultipleSearchParams([
        { name: "card_id", value: id },
        { name: "brd_id", value: board_id },
      ]);
      openDetailCard?.();
    } else if (!isAssignedUser) {
      const url = `/board/${boardKey || boardUniqueKey}/${
        boardKey || boardUniqueKey
      }-${id}?${searchParams.toString()}`;

      if (isModifierPressed) {
        window.open(url, "_blank");
        event.stopPropagation();
        return;
      }
      navigate(url);
    } else {
      event.stopPropagation();
    }
  };

  const { user_id: currentUserId } = currentUser || {};
  const { data: { data: { responseData: { owner_user_id } = {} } = {} } = {} } =
    cachedData || {};

  const checkOwnerOrAdmin = isMyTaskCard
    ? isAdmin() || currentUserId === board_admin || boardPermission?.is_admin
    : isAdmin() ||
      currentUserId === owner_user_id ||
      boardPermission?.is_admin ||
      cachedData?.data?.data?.responseData?.is_owner;

  const accessDragCard =
    !isMyTaskCard &&
    (checkOwnerOrAdmin ||
      currentUserId === assignedUserId ||
      currentUserId === reporter);

  const accessForEditCard = checkOwnerOrAdmin || currentUserId === reporter;

  const isAssignee = currentUserId === assignedUserId;

  const accessForAssign =
    checkOwnerOrAdmin ||
    currentUserId === reporter ||
    isAssignee ||
    is_card_collaborator;

  const accessForChangeStatus =
    isMyTaskCard &&
    (checkOwnerOrAdmin ||
      currentUserId === reporter ||
      currentUserId === assignedUserId);

  const updateSearchParams = (key, value) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(key, value);
    setSearchParams(newSearchParams);
  };

  const openEditField = (e) => {
    e.stopPropagation();
    accessForEditCard && setIsEditing(true);
  };

  const cardContentProps = {
    project,
    boardName,
    isAssignedUser,
    style,
    isMyTaskCard,
    accessForChangeStatus,
    onDragEnd,
    handleDetails,
    tooltipName,
    name,
    accessForAssign,
    isTodo,
    checkOwnerOrAdmin,
    currentUser,
    accessForEditCard,
    isMobile,
    openEditField,
    isAssignee,
    boardId,
    openSubtask,
    isEditing,
    setIsEditing,
    collapsed,
    formatTimeFromNow,
    boardKey,
    is_card_collaborator,
    boardPermission,
  };

  return isMyTaskCard ? (
    <Tooltip title={board_name}>
      <CardContent {...cardContentProps} isMyPlanner={isMyPlanner} />
    </Tooltip>
  ) : (
    <Draggable
      key={id?.toString()}
      draggableId={card_id?.toString()}
      index={index}
      isDragDisabled={!accessDragCard || isTodo || (!project?.card?.assigned_to_user?.user_id==currentUser?.user_id && !boardPermission?.can_update_status && !isMyPlanner) || isMobile}
    >
      {(provided, snapshot) => (
        <CardContent
          {...cardContentProps}
          provided={provided}
          snapshot={snapshot}
          isMyPlanner={isMyPlanner}
        />
      )}
    </Draggable>
  );
}

function arePropsEqual(prevProps, nextProps) {
  const { project: prevProject, ...prevRest } = prevProps;
  const { project: nextProject, ...nextRest } = nextProps;

  // Deep comparison of the project object
  const isProjectEqual =
    prevProject.card_id === nextProject.card_id &&
    Object.keys(prevProject).every((key) => {
      if (typeof prevProject[key] === "object" && prevProject[key] !== null) {
        return (
          JSON.stringify(prevProject[key]) === JSON.stringify(nextProject[key])
        );
      }
      return prevProject[key] === nextProject[key];
    });

  // Check other props that should trigger re-render
  const areOtherPropsEqual = Object.keys(prevRest).every(
    (key) => prevRest[key] === nextRest[key]
  );

  return isProjectEqual && areOtherPropsEqual;
}

export default React.memo(KanbanCard, arePropsEqual);
